// components/RHFPasswordField.tsx
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import type { GeneralFormProps } from '@/types/general';

import { Button } from '../ui/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '../ui/form';
import { Icon } from '../ui/icon';
import { Input } from '../ui/input';

type Props = {
  showVisibilityToggle?: boolean;
  size?: 'sm' | 'default' | 'lg' | null | undefined;
} & GeneralFormProps;

function RHFPasswordField({
  name,
  title,
  description,
  placeholder,
  value: externalValue,
  onChange: externalOnChange,
  required,
  disabled,
  size = 'default',
  showVisibilityToggle = true,
}: Props) {
  const { control } = useFormContext<Record<string, string>>();
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="mt-4 w-full">
          <FormControl>
            <div className="relative">
              <Input
                {...field}
                inputSize={size}
                disabled={disabled}
                startIcon="material-symbols:lock"
                required={required}
                data-testid={field.name}
                type={showPassword === true ? 'text' : 'password'}
                placeholder={placeholder}
                title={title}
                onChange={(e) => {
                  const value = String(e.target.value);
                  const newEvent = { ...e, target: { ...e.target, value } };

                  if (externalOnChange) {
                    externalOnChange(newEvent);
                    return;
                  }
                  field.onChange(newEvent);
                }}
                value={field.value ?? externalValue ?? ''}
              />
              {showVisibilityToggle === true && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-1/2 -translate-y-1/2 h-full px-3 hover:bg-transparent"
                  onClick={togglePasswordVisibility}
                  disabled={disabled}
                >
                  <Icon
                    icon={showPassword === true ? 'material-symbols:visibility-off' : 'material-symbols:visibility'}
                    className="h-5 w-5 text-muted-foreground"
                  />
                </Button>
              )}
            </div>
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export default RHFPasswordField;
